import React from 'react';
import { cn } from '@/lib/twUtils';

const tones = {
  /** Bianco: sezione di respiro */
  paper: 'bg-white text-ink',
  /** Grigio azzurrino: sezione alternata */
  mist: 'bg-mist text-ink',
  /** Navy pieno: bande scure */
  navy: 'bg-primary text-white',
  /** Navy profondo: footer */
  deep: 'bg-navy-deep text-white',
} as const;

const paddings = {
  default: 'section-y',
  sm: 'section-y-sm',
  /** La sezione gestisce da sé lo spazio verticale */
  none: '',
} as const;

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  tone?: keyof typeof tones;
  padding?: keyof typeof paddings;
  /** Linea hairline in cima, per separare due sezioni chiare adiacenti */
  divided?: boolean;
  /** Alone arancione decorativo: angolo in cui posizionarlo */
  glow?: 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right';
}

const glowPositions: Record<NonNullable<SectionProps['glow']>, string> = {
  'top-right': '-top-40 -right-32',
  'top-left': '-top-32 -left-36',
  'bottom-left': '-bottom-40 -left-40',
  'bottom-right': '-bottom-28 -right-28',
};

/**
 * Banda orizzontale a piena larghezza: definisce fondo, ritmo verticale e
 * decorazioni. Il contenuto va incartato in <Container>.
 */
const Section = ({
  tone = 'paper',
  padding = 'default',
  divided = false,
  glow,
  className,
  children,
  ...props
}: SectionProps) => (
  <section
    className={cn(
      'relative px-6',
      tones[tone],
      paddings[padding],
      divided && 'border-t border-edge-soft',
      glow && 'overflow-hidden',
      className
    )}
    {...props}
  >
    {glow && (
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute h-[34rem] w-[34rem] rounded-full',
          'bg-[radial-gradient(circle,rgb(234_88_12/0.22),transparent_68%)]',
          glowPositions[glow]
        )}
      />
    )}
    {glow ? <div className="relative">{children}</div> : children}
  </section>
);

export default Section;
